import type { RemoteAudioTrackStats as TwilioRemoteAudioTrackStats } from 'twilio-video';

import { UserData } from '../../../../types';
import {
  RemoteAudioDelegator,
  RemoteDataDelegator,
  RemoteMediaDelegator,
  RemoteVideoDelegator,
} from './media/MediaDelegator';
import { MediaKind } from './media/types';

type RemoteParticipantAudioVideoEventArgs = [remoteMediaDelegator: RemoteMediaDelegator];

/**
 * Both Twilio and Zoom returns a number between 0 - 5
 * 5	Excellent network
 * 4	Good network
 * 3	Average network
 * 2	Below average network
 * 1	Bad network
 * 0	Network broken (reconnecting)
 *
 * https://www.twilio.com/docs/video/using-network-quality-api
 * https://developers.zoom.us/docs/video-sdk/web/quality
 * - There's a nuanced difference with Zoom, where it specifics uplink and downlink
 *
 * networkQualityStats is unknown to support Twilio's value.
 * Zoom doesn't have an equivalent.
 */
type ParticipantNetworkQualityLevel = number;
export type NetworkQualityChangeHandler = (
  networkQualityLevel: ParticipantNetworkQualityLevel,
  networkQualityStats?: unknown,
) => void;

/**
 * Defines the events and event handlers for our core events.
 * Vendor specific implementations will map from our standardized state.
 * This includes all events that are actively used.
 */
export type RemoteParticipantEventArgsMap = {
  mediaSubscribed: RemoteParticipantAudioVideoEventArgs;
  mediaUnsubscribed: RemoteParticipantAudioVideoEventArgs;
  mediaSwitchedOff: RemoteParticipantAudioVideoEventArgs;
  mediaSwitchedOn: RemoteParticipantAudioVideoEventArgs;
  mediaEnabled: RemoteParticipantAudioVideoEventArgs;
  mediaSubscriptionFailed: [error: Error, remoteMediaDelegator: RemoteMediaDelegator];
  networkQualityLevelChanged: Parameters<NetworkQualityChangeHandler>;
};

/**
 * Both Twilio and Zoom use NodeJs EventEmitter for their event listeners.
 * The async handlers we've registered have been "fire-and-forget."
 */
export type RemoteParticipantEventMap = {
  [K in keyof RemoteParticipantEventArgsMap]: (...args: RemoteParticipantEventArgsMap[K]) => void | Promise<void>;
};

export type RemoteParticipantEvent = keyof RemoteParticipantEventArgsMap;
export type RemoteParticipantEventHandler<K extends RemoteParticipantEvent> = RemoteParticipantEventMap[K];

export type EventContext = {
  mediaKind: MediaKind;
};

// these events have the same typed event handler
export type RemoteParticipantMediaEventMap = Pick<
  RemoteParticipantEventMap,
  'mediaSubscribed' | 'mediaUnsubscribed' | 'mediaSwitchedOff' | 'mediaSwitchedOn'
>;

export type RemoteParticipantMediaEventHandler = RemoteParticipantEventMap['mediaSubscribed'];
export type RemoteParticipantMediaFailureEventHandler = RemoteParticipantEventMap['mediaSubscriptionFailed'];

// these are the only event we gracefully handle
export type RemoteParticipantMutexEventMap = Pick<
  RemoteParticipantEventMap,
  'mediaSubscribed' | 'mediaUnsubscribed' | 'mediaEnabled'
>;

export type RemoteParticipantMutexEventHandler<K extends keyof RemoteParticipantMutexEventMap> =
  RemoteParticipantMutexEventMap[K];

/**
 * Historical types, migrated here from other directories
 */

export type RemoteParticipantMediaByParticipantId = Record<
  string,
  {
    video?: RemoteVideoDelegator[];
    audio?: RemoteAudioDelegator[];
    data?: RemoteDataDelegator[];
  }
>;
export type userDataByParticipantId = Record<string, UserData>;
export type RemoteAudioStatsByTrackSid = Record<string, TwilioRemoteAudioTrackStats>;

export type ParticipantMediaStatusByParticipantId = Record<
  string,
  {
    isAudioMuted?: boolean;
    isVideoOff?: boolean;
  }
>;

export type RemoteMicrophoneByParticipantId = Record<string, RemoteAudioDelegator>;

export enum ParticipantConnectionState {
  Connected = 'connected',
  Reconnecting = 'reconnecting',
}
